import type { RoiGeometry } from "@gazelab/shared";

/** Tamaño mínimo (normalizado) para aceptar un rectángulo dibujado. */
export const MIN_ROI_SIZE = 0.01;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Normaliza un arrastre entre dos puntos (coordenadas de contenido 0–1, posiblemente fuera de rango)
 * a un rectángulo dentro de la imagen. Devuelve null si es demasiado pequeño.
 */
export function rectFromDrag(a: { x: number; y: number }, b: { x: number; y: number }): RoiGeometry | null {
  const x0 = clamp01(Math.min(a.x, b.x));
  const x1 = clamp01(Math.max(a.x, b.x));
  const y0 = clamp01(Math.min(a.y, b.y));
  const y1 = clamp01(Math.max(a.y, b.y));
  const width = x1 - x0;
  const height = y1 - y0;
  if (width < MIN_ROI_SIZE || height < MIN_ROI_SIZE) return null;
  return { x: x0, y: y0, width: Math.min(width, 1 - x0), height: Math.min(height, 1 - y0) };
}

/**
 * Proyecta un punto de un panel que muestra la imagen con `object-fit: contain` a coordenadas
 * normalizadas de la imagen (sin recortar; puede quedar fuera de 0–1).
 */
export function panelPointToImage(
  px: number,
  py: number,
  panel: { width: number; height: number },
  image: { naturalWidth: number; naturalHeight: number },
): { x: number; y: number } {
  const r = containRect(panel.width, panel.height, image.naturalWidth, image.naturalHeight);
  return { x: (px - r.left) / r.width, y: (py - r.top) / r.height };
}

export function containRect(cw: number, ch: number, nw: number, nh: number) {
  const scale = Math.min(cw / nw, ch / nh);
  const width = nw * scale;
  const height = nh * scale;
  return { left: (cw - width) / 2, top: (ch - height) / 2, width, height };
}
